import { CodeBlock, SectionTitle, SubTitle, InfoBox } from './shared';

export default function SessionContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">会话</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          会话（Session）是 OpenClaw 保存对话历史的核心机制，Gateway 是唯一的数据源。
        </p>
      </div>

      <SectionTitle id="sess-gateway">Gateway 是唯一数据源</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        所有会话状态都由 Gateway 统一管理，智能体本身是无状态的。这意味着：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>智能体重启后会话历史不会丢失</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>多个智能体实例可以共享同一个会话存储</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>会话数据可以独立备份和迁移</span></li>
      </ul>

      <SectionTitle id="sess-storage">状态存储位置</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        会话数据存储在工作区的 <code className="text-cyan-400 bg-slate-800/60 px-1 rounded text-xs">sessions/</code> 目录中，每个对等方（用户/群组）对应一个 JSON 文件：
      </p>
      <CodeBlock language="bash" code={`~/.openclaw/agents/<agent-id>/sessions/
├── telegram_user_123456.json
├── telegram_group_789012.json
├── whatsapp_+1234567890.json
└── discord_user_345678.json`} />
      <CodeBlock language="json5" code={`// 会话文件结构示例
{
  peerId: "telegram_user_123456",
  channelType: "telegram",
  createdAt: "2024-01-01T00:00:00Z",
  updatedAt: "2024-01-15T12:30:00Z",
  messages: [
    { role: "user", content: "你好", timestamp: 1700000000000 },
    { role: "assistant", content: "你好！有什么可以帮助你的？", timestamp: 1700000001000 },
    // ...更多消息
  ],
  compactionSummary: "用户询问了关于天气的问题...",  // 压缩摘要（可选）
  metadata: {
    userName: "张三",
    language: "zh-CN",
  }
}`} />

      <SectionTitle id="sess-trim">会话修剪</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        当会话消息数量超过配置的上限时，系统会自动修剪旧消息：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    session: {
      // 保留的最大消息数
      maxMessages: 100,
      // 修剪策略：oldest（删除最旧）/ summarize（压缩摘要）
      trimStrategy: "summarize",
      // 触发修剪的阈值（消息数）
      trimThreshold: 80,
    }
  }
}`} />

      <SectionTitle id="sess-memory-flush">压缩前记忆刷新</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        在触发压缩之前，系统会先将重要信息写入记忆文件（memory.md），确保关键信息不会在压缩中丢失：
      </p>
      <div className="space-y-3 my-4">
        {[
          '检测到上下文窗口接近上限',
          '触发记忆刷新：提示模型将重要信息写入 memory.md',
          '等待记忆写入完成',
          '执行压缩：将历史消息压缩为摘要',
          '继续正常处理',
        ].map((step, i) => (
          <div key={i} className="flex items-start gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span className="text-slate-300 text-sm">{step}</span>
          </div>
        ))}
      </div>

      <SectionTitle id="sess-key-mapping">传输到会话键的映射</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        不同渠道的对等方 ID 映射规则：
      </p>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">渠道</th>
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">会话键格式</th>
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">示例</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Telegram 私信', 'telegram_user_{userId}', 'telegram_user_123456'],
              ['Telegram 群组', 'telegram_group_{groupId}', 'telegram_group_789012'],
              ['WhatsApp 私信', 'whatsapp_{phoneNumber}', 'whatsapp_+8613800138000'],
              ['WhatsApp 群组', 'whatsapp_group_{groupId}', 'whatsapp_group_abc123'],
              ['Discord 私信', 'discord_user_{userId}', 'discord_user_345678'],
              ['Discord 频道', 'discord_channel_{channelId}', 'discord_channel_901234'],
            ].map(([channel, format, example], i) => (
              <tr key={i} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors">
                <td className="py-2.5 px-3 text-slate-300">{channel}</td>
                <td className="py-2.5 px-3"><code className="text-cyan-400 font-mono text-xs">{format}</code></td>
                <td className="py-2.5 px-3"><code className="text-slate-400 font-mono text-xs">{example}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SectionTitle id="sess-lifecycle">生命周期</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        会话的完整生命周期：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-add-circle-line text-emerald-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">创建：</strong>首次收到来自某个对等方的消息时自动创建</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-refresh-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">更新：</strong>每次对话后自动更新消息历史</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-compress-line text-amber-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">压缩：</strong>消息数量超限时自动压缩</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-delete-bin-line text-rose-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">删除：</strong>手动执行 /clear 命令或通过 CLI 删除</span></li>
      </ul>

      <SectionTitle id="sess-send-policy">发送策略</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        控制消息如何发送到会话：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    session: {
      sendPolicy: {
        // 是否将用户消息保存到会话
        saveUserMessages: true,
        // 是否将助手回复保存到会话
        saveAssistantMessages: true,
        // 是否保存工具调用记录
        saveToolCalls: false,
        // 是否保存系统消息
        saveSystemMessages: false,
      }
    }
  }
}`} />

      <SectionTitle id="sess-config">配置示例</SectionTitle>
      <CodeBlock language="json5" code={`{
  agent: {
    session: {
      maxMessages: 100,
      trimStrategy: "summarize",
      trimThreshold: 80,
      sendPolicy: {
        saveUserMessages: true,
        saveAssistantMessages: true,
      },
      // 会话过期时间（天），0 表示永不过期
      expiryDays: 0,
      // 是否启用会话隔离（不同渠道的同一用户使用不同会话）
      channelIsolation: true,
    }
  }
}`} />

      <SectionTitle id="sess-inspect">检查</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        使用 CLI 命令检查会话状态：
      </p>
      <CodeBlock language="bash" code={`# 列出所有会话
openclaw sessions list --agent my-agent

# 查看特定会话
openclaw sessions show --agent my-agent --peer telegram_user_123456

# 清除特定会话
openclaw sessions clear --agent my-agent --peer telegram_user_123456

# 导出会话
openclaw sessions export --agent my-agent --output sessions-backup.json`} />

      <SectionTitle id="sess-tips">提示</SectionTitle>
      <InfoBox type="tip">
        定期备份会话数据，特别是在升级 OpenClaw 版本之前。使用 <code className="text-emerald-400 bg-slate-800/60 px-1 rounded text-xs">openclaw sessions export</code> 命令导出所有会话。
      </InfoBox>
      <InfoBox type="info">
        如果用户反映智能体"忘记"了之前的对话，可以检查会话文件是否存在，以及 <code className="text-cyan-400 bg-slate-800/60 px-1 rounded text-xs">maxMessages</code> 配置是否合理。
      </InfoBox>

      <SectionTitle id="sess-source-meta">会话来源元数据</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        每个会话都包含来源元数据，记录消息的渠道信息：
      </p>
      <CodeBlock language="json5" code={`{
  sourceMetadata: {
    channelType: "telegram",
    channelId: "bot_123456",
    peerId: "user_789012",
    peerType: "private",  // private / group / channel
    peerName: "张三",
    peerUsername: "zhangsan",
    language: "zh-CN",
    timezone: "Asia/Shanghai",
  }
}`} />
    </div>
  );
}
